import { useCallback, useEffect, useMemo, useState } from "react"

import {
  api,
  type AlcanceDeConfiguracoes,
  type EntidadeNaLista,
  type RespostaDosSetores,
  type SetorNoCadastro,
  type TipoDeSetorNaTela,
} from "../api"
import { Abas, type DescricaoDeAba } from "../componentes/Abas"
import { mensagemDeRecusa } from "../componentes/BlocoDeCampos"
import { Button } from "../componentes/Button"
import { Card, CardContent, CardHeader, CardTitle } from "../componentes/Card"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../componentes/AlertDialog"
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "../componentes/Empty"
import { Skeleton } from "../componentes/Skeleton"
import { toast } from "../componentes/Toast"
import * as Icones from "../componentes/Icones"
import { Input } from "../componentes/Input"
import { Escolha } from "../componentes/Escolha"
import { ComDica } from "../componentes/ComDica"
import { FiltroAplicado } from "../componentes/FiltroAplicado"
import { RotuloDoCampo, faltaNoFormulario } from "../componentes/RotuloDoCampo"
import { Selo } from "../componentes/Selo"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../componentes/Sheet"
import { Acao, Column, TablePrincipal2, TablePrincipalFiltroGlobal } from "../componentes/TablePrincipal2"
import { inteiro } from "../formato"
import { BOTAO_BRANCO, BOTAO_CANCELAR, CABECALHO_DO_SHEET, MAIUSCULA, PROCEDENCIA, RECUSA } from "../lib/classes"
import { cn } from "../lib/cn"

type AbaDosSetores = "todos" | "ativos" | "inativos"

const ABAS_DOS_SETORES: DescricaoDeAba<AbaDosSetores>[] = [
  { chave: "todos", rotulo: "Todos", dica: "Mostra todos os setores da entidade" },
  { chave: "ativos", rotulo: "Ativos", dica: "Mostra só os setores que aparecem na lista de lotação" },
  { chave: "inativos", rotulo: "Inativos", dica: "Mostra só os setores desativados" },
]

const POR_PAGINA = 25
const CHAVE_DA_ENTIDADE = "contratos.setores.entidade"
const SEPARADOR = " › "

const RECUO = ["pl-0", "pl-5 max-md:pl-3", "pl-10 max-md:pl-6", "pl-15 max-md:pl-9", "pl-20 max-md:pl-12", "pl-25 max-md:pl-15"]

type Gaveta = { modo: "novo"; superiorId: number | null } | { modo: "editar"; setor: SetorNoCadastro } | null

type LinhaDaArvore = SetorNoCadastro & { nivel: number; temFilhos: boolean; superiores: string }

const normalizar = (texto: string) =>
  texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .replace(/\s+/g, " ")
    .toUpperCase()

const paraMostrar = (texto: string) => texto.trim().replace(/\s+/g, " ").toLocaleUpperCase("pt-BR")

const contasQueContinuam = (quantas: number) =>
  quantas === 0
    ? "Nenhuma conta está lotada nele."
    : quantas === 1
      ? "A conta lotada nele continua lotada."
      : `As ${inteiro(quantas)} contas lotadas nele continuam lotadas.`

function lerEntidadeGuardada(): string {
  try {
    return localStorage.getItem(CHAVE_DA_ENTIDADE) ?? ""
  } catch {
    return ""
  }
}

function guardarEntidade(valor: string) {
  try {
    localStorage.setItem(CHAVE_DA_ENTIDADE, valor)
  } catch {
    return
  }
}

function descendentes(setores: SetorNoCadastro[], id: number): Set<number> {
  const achados = new Set<number>()
  const pendentes = [id]
  while (pendentes.length > 0) {
    const atual = pendentes.pop()
    for (const setor of setores) {
      if (setor.superior_id === atual && !achados.has(setor.id)) {
        achados.add(setor.id)
        pendentes.push(setor.id)
      }
    }
  }
  return achados
}

function montarArvore(setores: SetorNoCadastro[]): LinhaDaArvore[] {
  const porId = new Map(setores.map((s) => [s.id, s]))
  const nivelDe = (setor: SetorNoCadastro): number => {
    let nivel = 0
    let atual = setor.superior_id === null ? undefined : porId.get(setor.superior_id)
    const vistos = new Set<number>()
    while (atual !== undefined && !vistos.has(atual.id)) {
      vistos.add(atual.id)
      nivel += 1
      atual = atual.superior_id === null ? undefined : porId.get(atual.superior_id)
    }
    return nivel
  }
  return setores.map((s) => ({
    ...s,
    nivel: nivelDe(s),
    temFilhos: setores.some((outro) => outro.superior_id === s.id),
    superiores: s.caminho.split(SEPARADOR).slice(0, -1).join(SEPARADOR),
  }))
}

export function Setores({
  alcance,
  tipo,
  aoFiltrarTipo,
}: {
  alcance: AlcanceDeConfiguracoes
  tipo?: string
  aoFiltrarTipo?: (tipo: string | null) => void
}) {
  const eRoot = alcance.conta_de_plataforma
  const [entidades, definirEntidades] = useState<EntidadeNaLista[] | null>(null)
  const [entidadeId, definirEntidadeId] = useState<string>(eRoot ? lerEntidadeGuardada() : "")
  const [resposta, definirResposta] = useState<RespostaDosSetores | null>(null)
  const [tipos, definirTipos] = useState<TipoDeSetorNaTela[]>([])
  const [falha, definirFalha] = useState<string | null>(null)
  const [busca, definirBusca] = useState("")
  const [aba, definirAba] = useState<AbaDosSetores>("todos")
  const [pagina, definirPagina] = useState(1)
  const [porPagina, definirPorPagina] = useState(POR_PAGINA)
  const [recolhidos, definirRecolhidos] = useState<Set<number>>(new Set())
  const [gaveta, definirGaveta] = useState<Gaveta>(null)
  const [desativando, definirDesativando] = useState<SetorNoCadastro | null>(null)

  useEffect(() => {
    api
      .tiposDeSetor()
      .then((r) => definirTipos(r.dados))
      .catch(() => definirTipos([]))
  }, [])

  useEffect(() => {
    if (!eRoot) return
    api
      .entidades("")
      .then((r) => {
        definirEntidades(r.dados)
        definirEntidadeId((atual) => (r.dados.some((e) => String(e.id) === atual) ? atual : String(r.dados[0]?.id ?? "")))
      })
      .catch((erro: unknown) => definirFalha(mensagemDeRecusa(erro)))
  }, [eRoot])

  const [tentativa, definirTentativa] = useState(0)

  useEffect(() => {
    if (eRoot && entidadeId === "") return
    let vivo = true
    definirResposta(null)
    definirFalha(null)
    api
      .cadastroDeSetores(eRoot ? Number(entidadeId) : undefined)
      .then((r) => {
        if (!vivo) return
        definirResposta(r)
        definirFalha(null)
      })
      .catch((erro: unknown) => vivo && definirFalha(mensagemDeRecusa(erro)))
    return () => {
      vivo = false
    }
  }, [eRoot, entidadeId, tentativa])

  const aplicar = useCallback((r: RespostaDosSetores) => {
    definirResposta(r)
    definirFalha(null)
  }, [])

  const arvore = useMemo(() => montarArvore(resposta?.dados ?? []), [resposta])

  const escolhaDaEntidade = eRoot && (
    <CardContent>
      <div className="max-w-md">
        <RotuloDoCampo htmlFor="setores-entidade">Entidade</RotuloDoCampo>
        <Escolha
          id="setores-entidade"
          valor={entidadeId}
          desabilitado={entidades === null}
          vazio={entidades === null ? "Carregando..." : undefined}
          placeholderDaBusca="Buscar por sigla ou nome"
          aoMudar={(novo) => {
            definirEntidadeId(novo)
            guardarEntidade(novo)
            definirRecolhidos(new Set())
            definirPagina(1)
          }}
          opcoes={(entidades ?? []).map((entidade) => ({ valor: String(entidade.id), rotulo: `${entidade.sigla} · ${entidade.nome}` }))}
        />
      </div>
    </CardContent>
  )

  if (resposta === null) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Setores</CardTitle>
        </CardHeader>
        {escolhaDaEntidade}
        {falha !== null ? (
          <CardContent className="flex flex-wrap items-center gap-3">
            <p role="alert" className={cn(RECUSA, MAIUSCULA)}>
              {falha}
            </p>
            <Button type="button" variant="outline" size="sm" className={BOTAO_BRANCO} onClick={() => definirTentativa((n) => n + 1)}>
              <Icones.Recalcular />
              Tentar de novo
            </Button>
          </CardContent>
        ) : (
          <CardContent aria-busy="true" className="flex flex-col gap-3">
            <Skeleton className="h-10 w-1/2" />
            {[0, 1, 2, 3, 4].map((linha) => (
              <Skeleton key={linha} className="h-9 w-full" />
            ))}
          </CardContent>
        )}
      </Card>
    )
  }

  const podeAlterar = resposta.pode_alterar
  const setores = resposta.dados
  const procurado = normalizar(busca)
  const naAba = (s: SetorNoCadastro) => (aba === "todos" || (aba === "ativos") === s.ativo) && (tipo === undefined || s.tipo === tipo)
  const escondidos = new Set<number>()
  for (const id of recolhidos) for (const filho of descendentes(setores, id)) escondidos.add(filho)
  const visiveis =
    procurado === ""
      ? arvore.filter((s) => naAba(s) && !escondidos.has(s.id))
      : arvore.filter(
          (s) => naAba(s) && (normalizar(s.nome).includes(procurado) || normalizar(s.sigla ?? "").includes(procurado)),
        )
  const totalDePaginas = Math.max(1, Math.ceil(visiveis.length / porPagina))
  const paginaAtual = Math.min(pagina, totalDePaginas)
  const recorte = visiveis.slice((paginaAtual - 1) * porPagina, paginaAtual * porPagina)
  const emArvore = procurado === "" && aba === "todos" && tipo === undefined

  const alternar = (id: number) =>
    definirRecolhidos((atual) => {
      const novo = new Set(atual)
      if (novo.has(id)) novo.delete(id)
      else novo.add(id)
      return novo
    })

  const mudar = (setor: SetorNoCadastro, ativo: boolean) => {
    api
      .alterarSetor(setor.id, { ativo })
      .then((r) => {
        aplicar(r)
        toast.success(`Setor ${setor.nome} ${ativo ? "reativado" : "desativado"}`)
      })
      .catch((erro: unknown) => toast.error(mensagemDeRecusa(erro)))
  }

  const abrirGaveta = (nova: Gaveta) => definirGaveta(nova)

  const motivoParaNaoDesativar = (alvo: SetorNoCadastro): string | undefined => {
    const filhosAtivos = setores.filter((s) => s.superior_id === alvo.id && s.ativo)
    if (filhosAtivos.length > 0) return `Tem subsetores ativos: ${filhosAtivos.map((s) => s.nome).join(", ")}`
    if (!setores.some((s) => s.id !== alvo.id && s.ativo)) return "É o último setor ativo"
    return undefined
  }

  const comFilhos = arvore.filter((s) => s.temFilhos).map((s) => s.id)
  const tudoRecolhido = comFilhos.length > 0 && comFilhos.every((id) => recolhidos.has(id))

  const vazio =
    setores.length === 0 ? (
      <Empty className="py-8">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icones.Organograma />
          </EmptyMedia>
          <EmptyTitle>Nenhum setor cadastrado</EmptyTitle>
          <EmptyDescription>
            {podeAlterar ? "Cadastre o primeiro setor, onde as contas serão lotadas." : "Quem administra a entidade cadastra os setores."}
          </EmptyDescription>
        </EmptyHeader>
        {podeAlterar && (
          <EmptyContent>
            <Button type="button" className="h-10" onClick={() => abrirGaveta({ modo: "novo", superiorId: null })}>
              <Icones.Mais />
              Adicionar setor
            </Button>
          </EmptyContent>
        )}
      </Empty>
    ) : (
      <Empty className="py-8">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icones.Buscar />
          </EmptyMedia>
          <EmptyTitle>Nenhum setor encontrado</EmptyTitle>
          <EmptyDescription>{busca !== "" ? `Nada casa com "${busca}".` : "Nenhum setor nesta situação."}</EmptyDescription>
        </EmptyHeader>
        {busca !== "" && (
          <EmptyContent>
            <Button type="button" variant="outline" size="sm" className={BOTAO_BRANCO} onClick={() => definirBusca("")}>
              Limpar a busca
            </Button>
          </EmptyContent>
        )}
      </Empty>
    )

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Setores{" "}
          <ComDica dica="Os setores organizam a entidade e são a lotação das contas. Se desativado, sai da lista de lotação, e as contas lotadas nele continuam." />
        </CardTitle>
      </CardHeader>

      {escolhaDaEntidade}

      <CardContent>
        <TablePrincipal2
          data={recorte}
          pageCount={totalDePaginas}
          currentPage={paginaAtual}
          pageSize={porPagina}
          totalRegistros={visiveis.length}
          onPageChange={definirPagina}
          onPageSizeChange={(tamanho) => {
            definirPorPagina(tamanho)
            definirPagina(1)
          }}
          globalFilterValue={busca}
          onGlobalFilterChange={(valor) => {
            definirBusca(valor)
            definirPagina(1)
          }}
          mensagemVazia={vazio}
          listrada
          aoClicarNaLinha={podeAlterar ? (setor) => abrirGaveta({ modo: "editar", setor }) : undefined}
          filtrosNaBarra={
            tipo !== undefined ? (
              <FiltroAplicado rotulo="Tipo" valor={tipo} aoLimpar={() => aoFiltrarTipo?.(null)} />
            ) : (
            emArvore &&
            comFilhos.length > 0 && (
              <Button
                type="button"
                variant="outline"
                className={cn("h-10", BOTAO_BRANCO)}
                onClick={() => definirRecolhidos(tudoRecolhido ? new Set() : new Set(comFilhos))}
              >
                {tudoRecolhido ? <Icones.Aumentar tamanho={16} /> : <Icones.Diminuir tamanho={16} />}
                {tudoRecolhido ? "Expandir subsetores" : "Esconder subsetores"}
              </Button>
            )
            )
          }
          acaoNoTopo={
            podeAlterar && (
              <Button type="button" className="h-10" onClick={() => abrirGaveta({ modo: "novo", superiorId: null })}>
                <Icones.Mais />
                Adicionar setor
              </Button>
            )
          }
          abasNaBarra={
            <Abas
              nome="setores"
              rotulo="Situação dos setores"
              classe="mb-0"
              abas={ABAS_DOS_SETORES}
              atual={aba}
              aoTrocar={(chave) => {
                definirAba(chave)
                definirPagina(1)
              }}
            />
          }
        >
          <TablePrincipalFiltroGlobal globalFilter={busca} setGlobalFilter={definirBusca} placeholder="Nome ou sigla" />
          <Column<LinhaDaArvore>
            name="nome"
            label="Setor"
            enableSorting={false}
            render={(_valor, setor) => (
              <div data-nivel={setor.nivel} className={cn("flex items-center gap-1", emArvore && RECUO[Math.min(setor.nivel, RECUO.length - 1)])}>
                {emArvore &&
                  (setor.temFilhos ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`${recolhidos.has(setor.id) ? "Expandir" : "Recolher"} ${setor.nome}`}
                      title={`${recolhidos.has(setor.id) ? "Expandir" : "Recolher"} ${setor.nome}`}
                      aria-expanded={!recolhidos.has(setor.id)}
                      onClick={() => alternar(setor.id)}
                    >
                      {recolhidos.has(setor.id) ? <Icones.Proxima /> : <Icones.Aumentar />}
                    </Button>
                  ) : (
                    <span aria-hidden="true" className="inline-block size-8 shrink-0" />
                  ))}
                <div className="min-w-0">
                  <span data-slot="nome-do-setor" className="font-medium">
                    {setor.nome}
                  </span>
                  {!emArvore && setor.superiores !== "" && <p className={PROCEDENCIA}>{setor.superiores}</p>}
                </div>
              </div>
            )}
          />
          <Column<LinhaDaArvore> name="sigla" label="Sigla" enableSorting={false} render={(valor) => (valor ? String(valor) : "—")} />
          <Column<LinhaDaArvore> name="tipo" label="Tipo" enableSorting={false} render={(valor) => (valor ? String(valor) : "—")} />
          <Column<LinhaDaArvore> name="contas" label="Contas" enableSorting={false} render={(valor) => inteiro(Number(valor))} />
          <Column<LinhaDaArvore>
            name="ativo"
            label="Situação"
            enableSorting={false}
            render={(_valor, setor) => <Selo situacao={setor.ativo ? "ativa" : "inativa"}>{setor.ativo ? "Ativo" : "Inativo"}</Selo>}
          />
          {podeAlterar && (
            <Acao<LinhaDaArvore> label="Editar" icon={Icones.Anotacao} onClick={(setor) => abrirGaveta({ modo: "editar", setor })} />
          )}
          {podeAlterar && (
            <Acao<LinhaDaArvore>
              label="Adicionar subsetor"
              icon={Icones.Mais}
             
              visivel={(setor) => setor.ativo}
              onClick={(setor) => abrirGaveta({ modo: "novo", superiorId: setor.id })}
            />
          )}
          {podeAlterar && (
            <Acao<LinhaDaArvore>
              label="Desativar"
              icon={Icones.Dispensar}
              visivel={(setor) => setor.ativo}
              disabled={(setor) => motivoParaNaoDesativar(setor) !== undefined}
              motivo={motivoParaNaoDesativar}
              onClick={definirDesativando}
            />
          )}
          {podeAlterar && (
            <Acao<LinhaDaArvore> label="Reativar" icon={Icones.Reabrir} visivel={(setor) => !setor.ativo} onClick={(setor) => mudar(setor, true)} />
          )}
        </TablePrincipal2>
      </CardContent>

      <AlertDialog open={desativando !== null} onOpenChange={(aberto) => !aberto && definirDesativando(null)}>
        <AlertDialogContent>
          <AlertDialogHeader tom="perigo">
            <AlertDialogTitle>Desativar o setor {desativando?.nome}?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              Ele sai da lista de lotação. {contasQueContinuam(desativando?.contas ?? 0)} É possível reativar depois.
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button type="button" variant="outline">
                <Icones.Fechar />
                Cancelar
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  if (desativando !== null) mudar(desativando, false)
                  definirDesativando(null)
                }}
              >
                <Icones.Dispensar />
                Desativar
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Sheet open={gaveta !== null} onOpenChange={(aberta) => !aberta && definirGaveta(null)}>
        <SheetContent
          aria-describedby={undefined}
          className="w-full gap-0 overflow-y-auto bg-card p-0 sm:max-w-lg [&>button:last-child]:text-primary-foreground [&>button:last-child]:opacity-100"
        >
          <SheetHeader className={CABECALHO_DO_SHEET}>
            <SheetTitle className="text-primary-foreground">
              {gaveta?.modo === "editar" ? `Setor: ${gaveta.setor.nome}` : "Novo setor"}
            </SheetTitle>
          </SheetHeader>
          {gaveta !== null && (
            <FormularioDoSetor
              key={gaveta.modo === "editar" ? gaveta.setor.id : `novo-${gaveta.superiorId ?? "topo"}`}
              inicial={gaveta.modo === "editar" ? gaveta.setor : null}
              superiorInicial={gaveta.modo === "editar" ? gaveta.setor.superior_id : gaveta.superiorId}
              setores={setores}
              tipos={tipos}
              entidadeId={eRoot ? Number(entidadeId) : undefined}
              aoCancelar={() => definirGaveta(null)}
              aoSalvar={(r, nome) => {
                aplicar(r)
                definirGaveta(null)
                toast.success(`Setor ${paraMostrar(nome)} salvo`)
              }}
            />
          )}
        </SheetContent>
      </Sheet>
    </Card>
  )
}

function FormularioDoSetor({
  inicial,
  superiorInicial,
  setores,
  tipos,
  entidadeId,
  aoCancelar,
  aoSalvar,
}: {
  inicial: SetorNoCadastro | null
  superiorInicial: number | null
  setores: SetorNoCadastro[]
  tipos: TipoDeSetorNaTela[]
  entidadeId: number | undefined
  aoCancelar: () => void
  aoSalvar: (resposta: RespostaDosSetores, nome: string) => void
}) {
  const [nome, definirNome] = useState(inicial?.nome ?? "")
  const [sigla, definirSigla] = useState(inicial?.sigla ?? "")
  const [tipoId, definirTipoId] = useState(inicial?.tipo_id === null || inicial === null ? "" : String(inicial.tipo_id))
  const [superiorId, definirSuperiorId] = useState(superiorInicial === null ? "" : String(superiorInicial))
  const [enviando, definirEnviando] = useState(false)
  const [recusa, definirRecusa] = useState<string | null>(null)

  const proibidos = inicial === null ? new Set<number>() : new Set([inicial.id, ...descendentes(setores, inicial.id)])
  const superiores = setores
    .filter((s) => s.ativo && !proibidos.has(s.id))
    .sort((a, b) => normalizar(a.caminho).localeCompare(normalizar(b.caminho), "pt-BR"))
  const tiposDeEscolha = tipos.filter((t) => t.ativo || t.id === inicial?.tipo_id)

  const superior = superiorId === "" ? null : (setores.find((s) => String(s.id) === superiorId) ?? null)
  const repetido = setores.find(
    (s) =>
      s.id !== inicial?.id &&
      s.superior_id === (superior?.id ?? null) &&
      normalizar(nome) !== "" &&
      normalizar(s.nome) === normalizar(nome),
  )
  const avisoDoRepetido = repetido ? `Já existe o setor ${repetido.nome} ${superior ? `em ${superior.nome}` : "no topo"}` : null
  const nomeLongo = nome.trim().length > 100
  const siglaLonga = sigla.trim().length > 45
  const falta = faltaNoFormulario([["o nome", nome]])
  const bloqueio =
    falta ??
    avisoDoRepetido ??
    (nomeLongo ? "O nome passa de 100 caracteres" : siglaLonga ? "A sigla passa de 45 caracteres" : null)

  const salvar = () => {
    definirEnviando(true)
    definirRecusa(null)
    const campos = {
      nome,
      sigla: sigla.trim() === "" ? null : sigla,
      tipo_id: tipoId === "" ? null : Number(tipoId),
      superior_id: superiorId === "" ? null : Number(superiorId),
    }
    const pedido =
      inicial === null
        ? api.criarSetor(entidadeId === undefined ? campos : { ...campos, entidade_id: entidadeId })
        : api.alterarSetor(inicial.id, campos)
    pedido
      .then((r) => aoSalvar(r, nome))
      .catch((erro: unknown) => definirRecusa(mensagemDeRecusa(erro)))
      .finally(() => definirEnviando(false))
  }

  return (
    <form
      className="flex flex-col gap-4 px-6 pt-3 pb-6"
      onSubmit={(evento) => {
        evento.preventDefault()
        if (bloqueio === null && !enviando) salvar()
      }}
    >
      <div>
        <RotuloDoCampo htmlFor="setor-nome" obrigatorio>
          Nome
        </RotuloDoCampo>
        <Input
          id="setor-nome"
          required
          autoComplete="off"
          maxLength={120}
          value={nome}
          aria-invalid={avisoDoRepetido !== null || nomeLongo || undefined}
          aria-describedby={avisoDoRepetido !== null || nomeLongo ? "setor-nome-aviso" : undefined}
          onChange={(evento) => definirNome(evento.target.value)}
        />
        {(avisoDoRepetido !== null || nomeLongo) && (
          <p id="setor-nome-aviso" className={cn(RECUSA, "mt-1.5")}>
            {avisoDoRepetido !== null ? `${avisoDoRepetido}.` : "O nome passa de 100 caracteres."}
          </p>
        )}
      </div>
      <div>
        <RotuloDoCampo htmlFor="setor-superior">Setor superior</RotuloDoCampo>
        <Escolha
          id="setor-superior"
          valor={superiorId}
          aoMudar={definirSuperiorId}
          vazio="Nenhum (setor de topo)"
          placeholderDaBusca="Buscar pelo nome do setor"
          opcoes={superiores.map((s) => ({ valor: String(s.id), rotulo: s.caminho }))}
        />
      </div>
      <div className="flex flex-wrap gap-4">
        <div className="flex-[1_1_140px]">
          <RotuloDoCampo htmlFor="setor-sigla">Sigla</RotuloDoCampo>
          <Input
            id="setor-sigla"
            autoComplete="off"
            maxLength={60}
            value={sigla}
            aria-invalid={siglaLonga || undefined}
            onChange={(evento) => definirSigla(evento.target.value)}
          />
          {siglaLonga && <p className={cn(RECUSA, "mt-1.5")}>A sigla passa de 45 caracteres.</p>}
        </div>
        <div className="flex-[2_1_200px]">
          <RotuloDoCampo htmlFor="setor-tipo">Tipo</RotuloDoCampo>
          <Escolha
            id="setor-tipo"
            valor={tipoId}
            aoMudar={definirTipoId}
            vazio="Nenhum"
            opcoes={tiposDeEscolha.map((t) => ({ valor: String(t.id), rotulo: t.tipo }))}
          />
        </div>
      </div>
      {recusa !== null && (
        <p role="alert" className={cn(RECUSA, MAIUSCULA)}>
          {recusa}
        </p>
      )}
      <div className="mt-1 flex justify-end gap-2 border-t border-(--borda) pt-4">
        <Button type="button" variant="outline" className={BOTAO_CANCELAR} onClick={aoCancelar}>
          <Icones.Fechar />
          Cancelar
        </Button>
        <Button type="submit" disabled={bloqueio !== null || enviando} title={bloqueio ?? undefined}>
          <Icones.Salvar />
          {enviando ? "Salvando..." : "Salvar"}
        </Button>
      </div>
    </form>
  )
}
